import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'multicrm.demo';

interface DemoContextValue {
  isDemo: boolean;
  setDemo: (value: boolean) => void;
}

const DemoContext = createContext<DemoContextValue | null>(null);

function readDemoMode(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function DemoProvider({ children }: { children: ReactNode }): ReactNode {
  const [isDemo, setIsDemo] = useState<boolean>(readDemoMode);

  const setDemo = useCallback((next: boolean) => {
    setIsDemo(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<DemoContextValue>(() => ({ isDemo, setDemo }), [isDemo, setDemo]);

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemo(): DemoContextValue {
  const context = useContext(DemoContext);
  if (!context) throw new Error('useDemo must be used within DemoProvider');
  return context;
}

// Utils to mask data
export function maskPhone(phone: string | null | undefined, isDemo: boolean): string {
  if (!phone) return '';
  if (!isDemo) return phone;
  // Replace the last 4 digits with '*'
  let digitsToMask = 4;
  let result = '';
  for (let i = phone.length - 1; i >= 0; i--) {
    const ch = phone.charAt(i);
    if (/\d/.test(ch) && digitsToMask > 0) {
      result = '*' + result;
      digitsToMask--;
    } else {
      result = ch + result;
    }
  }
  return result;
}

export function maskTTN(ttn: string | null | undefined, isDemo: boolean): string {
  if (!ttn) return '';
  if (!isDemo) return ttn;
  // Replace last 4 digits with ****
  return ttn.replace(/\d{4}$/, '****');
}

export function maskName(name: string | null | undefined, isDemo: boolean): string {
  if (!name) return '';
  if (!isDemo) return name;
  const parts = name.trim().split(/\s+/);
  if (parts.length > 1) {
    // Mask all parts except the first one
    return (
      parts[0] +
      ' ' +
      parts
        .slice(1)
        .map((p) => p.charAt(0) + '***')
        .join(' ')
    );
  }
  // If only one word, just mask from the second letter
  return name.charAt(0) + '***';
}

const storeMaskCache = new Map<string, string>();
let nextStoreNum = 1;

export function maskStoreName(name: string | null | undefined, isDemo: boolean): string {
  if (!name) return '';
  if (!isDemo) return name;
  let masked = storeMaskCache.get(name);
  if (!masked) {
    masked = `Магазин ${nextStoreNum++}`;
    storeMaskCache.set(name, masked);
  }
  return masked;
}

